import usePageTitle from "../utils/usePageTitle";
import { updateBrowserHash } from "../utils/urlHash";
import { usePageAsk } from "../chat/AskContext";
import { defaultAskSuggestions, pageDateFilters } from "../chat/askScope";
import React, { useEffect, useMemo } from "react";
import { useDashboardDates } from "../components/dashboard/useDashboardDates";
import { localDayBounds } from "../components/dashboard/dateSelection";
import { useTranslation } from "react-i18next";

import { formatApiRequestError, get_processes_list } from "../api";
import { useLogin } from "../reducers";
import { ProcessesSection } from "../components";
import { summariesForPeople } from "../components/processes/processPeople";
import { useKeyedRequest } from "../utils/useKeyedRequest";
import { useUserFilterSelection } from "../utils/useUserFilterSelection";

// Hash param key owned by this screen (registered in urlHash's HashParamKey).
export const PROCESS_USERS_HASH_KEY = "pu";
export const PROCESS_FROM_HASH_KEY = "pdf";
export const PROCESS_TO_HASH_KEY = "pdt";

const Processes = () => {
  const { t } = useTranslation();
  usePageTitle(t("processes"));
  const { user, permissions } = useLogin();
  const dates = useDashboardDates();
  const changeDates: typeof dates.onChange = (from, to, preset) => {
    dates.onChange(from, to, preset);
    updateBrowserHash({ page: null });
  };
  const dateKey = `${dates.from}:${dates.to}`;
  const dateQuery = useMemo(() => localDayBounds(dates.from, dates.to), [dates.from, dates.to]);
  const { selectedUserIds, userOptions, handleUserChange, loadingUsers } = useUserFilterSelection(
    PROCESS_USERS_HASH_KEY,
    permissions.processes.visualization,
  );
  const askDates = pageDateFilters(dates.from, dates.to);
  usePageAsk({
    sessionKey: "processes:list",
    page: { kind: "processes" },
    label: `${
      selectedUserIds.length === userOptions.length
        ? t("watchlist.all_people")
        : userOptions
            .filter(user => selectedUserIds.includes(user.id))
            .map(user => user.label)
            .join(", ") || t("watchlist.select_people")
    } · ${t("watchlist.all_processes")} · ${dates.from || dates.to ? `${dates.from} – ${dates.to}` : t("dashboard.all_time")}`,
    filters: { userIds: selectedUserIds, ...askDates },
    empty: loadingUsers || selectedUserIds.length === 0,
    suggestions: defaultAskSuggestions(t),
  });
  useEffect(() => {
    const handleTourFilterProcesses = (event: Event) => {
      handleUserChange((event as CustomEvent<{ userIds: string[] }>).detail.userIds);
    };
    window.addEventListener("tour:filter-processes", handleTourFilterProcesses);
    return () => window.removeEventListener("tour:filter-processes", handleTourFilterProcesses);
  }, [handleUserChange]);

  const allUserIds = useMemo(() => userOptions.map(user => user.id), [userOptions]);
  // Everyone the picker offers, broken down by person: picking people narrows it without asking
  // again, and its length is the page's total.
  const directory = useKeyedRequest(
    loadingUsers ? null : JSON.stringify([allUserIds, dateKey]),
    signal => get_processes_list({ user_ids: allUserIds, ...dateQuery }, signal),
  );
  const processes = useMemo(
    () => directory.data && summariesForPeople(directory.data, selectedUserIds),
    [directory.data, selectedUserIds],
  );
  const visibleError = directory.failed ? formatApiRequestError(directory.error, t) : null;

  return (
    <div data-tour="processes-page" className="dashboard-page">
      <ProcessesSection
        processes={processes ?? null}
        totalProcessCount={directory.data?.length ?? null}
        error={visibleError}
        activeUserIds={selectedUserIds}
        userOptions={userOptions}
        onUserChange={handleUserChange}
        showPeople={permissions.processes.visualization !== "OnlyMine"}
        personalView={selectedUserIds.length === 1 && selectedUserIds[0] === user.id}
        from={dates.from}
        to={dates.to}
        datePreset={dates.preset}
        rememberedCustomRange={dates.rememberedCustomRange}
        onDateChange={changeDates}
      />
    </div>
  );
};

export default Processes;
